import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, ModalBody, ModalFooter } from '@/components/ui/Modal/Modal';
import { ChangelogSection } from '@/hooks/useChangelog';

// ============================================================================
// Icons
// ============================================================================

const SparklesIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
  </svg>
);

// ============================================================================
// Inline Markdown rendering (bold text support)
// ============================================================================

function renderInlineMarkdown(text: string): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  const regex = /\*\*(.+?)\*\*/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    parts.push(
      <strong key={match.index} className="font-semibold text-gray-900">
        {match[1]}
      </strong>
    );
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts;
}

// ============================================================================
// Timeline Feature Item
// ============================================================================

const TimelineFeature: React.FC<{
  feature: { title: string; content: string[] };
  isLast: boolean;
}> = ({ feature, isLast }) => {
  return (
    <div className="relative flex gap-3">
      {/* Connector line + dot */}
      <div className="flex flex-col items-center">
        <div className="w-2 h-2 rounded-full bg-indigo-400 ring-2 ring-indigo-100 mt-1.5 flex-shrink-0" />
        {!isLast && <div className="w-px flex-1 bg-gray-200 mt-1" />}
      </div>

      {/* Content */}
      <div className={`flex-1 min-w-0 ${isLast ? '' : 'pb-4'}`}>
        <h4 className="text-sm font-semibold text-gray-900 leading-tight mb-1">
          {feature.title}
        </h4>
        <div className="space-y-1">
          {feature.content.map((line, i) => {
            const isListItem = line.startsWith('- ');
            const text = isListItem ? line.slice(2) : line;

            if (isListItem) {
              return (
                <div key={i} className="flex items-start gap-1.5 text-sm text-gray-600">
                  <span className="text-indigo-400 mt-0.5 flex-shrink-0">&bull;</span>
                  <span>{renderInlineMarkdown(text)}</span>
                </div>
              );
            }

            return (
              <p key={i} className="text-sm text-gray-600 leading-relaxed">
                {renderInlineMarkdown(text)}
              </p>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Timeline Version Section
// ============================================================================

const TimelineVersion: React.FC<{
  section: ChangelogSection;
  isLatest: boolean;
  isLastSection: boolean;
  isExpanded: boolean;
  onToggle: () => void;
}> = ({ section, isLatest, isLastSection, isExpanded, onToggle }) => {
  return (
    <div className="relative">
      {/* Version header row — clickable to toggle */}
      <button
        type="button"
        onClick={onToggle}
        className="flex items-center gap-3 mb-3 w-full text-left group"
        aria-expanded={isExpanded}
      >
        {/* Version dot — larger for version headers */}
        <div className="flex flex-col items-center">
          <div className={`w-3.5 h-3.5 rounded-full flex-shrink-0 ${
            isLatest
              ? 'bg-indigo-600 ring-4 ring-indigo-100'
              : 'bg-gray-300 ring-4 ring-gray-100'
          }`} />
        </div>

        {/* Version label */}
        <div className="flex items-center gap-2 flex-1">
          <span className={`text-sm font-bold ${isLatest ? 'text-indigo-700' : 'text-gray-500'} group-hover:underline`}>
            {section.version}
          </span>
          {section.date && (
            <span className="text-xs text-gray-400">{section.date}</span>
          )}
          {isLatest && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold bg-indigo-100 text-indigo-700">
              Novo
            </span>
          )}
        </div>

        {/* Chevron */}
        <svg
          className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={2}
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
        </svg>
      </button>

      {/* Features with timeline connector — collapsible */}
      <div
        className={`ml-[5px] ${isLastSection ? '' : 'border-l border-gray-200'} pl-5 overflow-hidden transition-[max-height,opacity] duration-300 ease-in-out ${
          isExpanded ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="space-y-0">
          {section.features.map((feature, idx) => (
            <TimelineFeature
              key={idx}
              feature={feature}
              isLast={idx === section.features.length - 1}
            />
          ))}
        </div>
        {/* Spacer between versions */}
        {!isLastSection && <div className="h-4" />}
      </div>
    </div>
  );
};

// ============================================================================
// Main ChangelogModal
// ============================================================================

export interface ChangelogModalProps {
  isOpen: boolean;
  onClose: () => void;
  sections: ChangelogSection[];
}

export const ChangelogModal: React.FC<ChangelogModalProps> = ({
  isOpen,
  onClose,
  sections,
}) => {
  const { t } = useTranslation();
  // Latest version expanded by default, others collapsed
  const [expandedVersions, setExpandedVersions] = useState<Set<string>>(() => {
    const initial = new Set<string>();
    if (sections.length > 0 && sections[0]) {
      initial.add(sections[0].version);
    }
    return initial;
  });

  const toggleVersion = (version: string) => {
    setExpandedVersions(prev => {
      const next = new Set(prev);
      if (next.has(version)) {
        next.delete(version);
      } else {
        next.add(version);
      }
      return next;
    });
  };

  if (sections.length === 0) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      animation="scale"
      closeOnBackdrop={false}
      ariaLabel={t('changelog.title')}
    >
      {/* Gradient header */}
      <div className="relative overflow-hidden rounded-t-lg">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-600 via-purple-600 to-indigo-800" />
        <div className="relative px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
              <SparklesIcon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">
                {t('changelog.title')}
              </h2>
              <p className="text-indigo-200 text-xs">
                {t('changelog.subtitle')}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-white/60 hover:text-white transition-colors p-1 rounded"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Timeline content */}
      <ModalBody className="p-0">
        <div className="px-6 py-5 max-h-[60vh] overflow-y-auto">
          {sections.map((section, idx) => (
            <TimelineVersion
              key={section.version}
              section={section}
              isLatest={idx === 0}
              isLastSection={idx === sections.length - 1}
              isExpanded={expandedVersions.has(section.version)}
              onToggle={() => toggleVersion(section.version)}
            />
          ))}
        </div>
      </ModalBody>

      {/* Footer */}
      <ModalFooter className="border-t border-gray-100 bg-gray-50/50 rounded-b-lg">
        <button
          type="button"
          onClick={onClose}
          className="w-full sm:w-auto px-6 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg hover:from-indigo-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 shadow-md shadow-indigo-200 transition-colors duration-200"
        >
          {t('changelog.dismiss')}
        </button>
      </ModalFooter>
    </Modal>
  );
};

export default ChangelogModal;
